import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import { FC, useMemo } from 'react'

import Dropdown, { DropdownItem } from './Dropdown'
import IconButton from './IconButton'

interface BasePaginationProps {
    /**
     * Current page number (1-indexed)
     */
    currentPage: number
    /**
     * Number of items per page (default: 50)
     */
    itemsPerPage?: number
    /**
     * Callback when page changes
     */
    onPageChange: (page: number) => void
    /**
     * Custom styles
     */
    sx?: SxProps
    /**
     * If true, show loading state and disable pagination controls
     */
    loading: boolean
    /**
     * Actual number of items on the current page.
     * Used to display accurate count when fewer items than itemsPerPage.
     * Shows loading state ("...") when changing pages.
     * Example: With 7 items, shows "1-7" instead of "1-50"
     */
    currentPageItemCount: number
    /**
     * Label used in the range display: "X-Y of Z"
     *
     * This should usually be a single short connector word without trailing
     * punctuation. Defaults to "of".
     */
    ofLabel?: string
    /**
     * Label used for "go to newest" action in the dropdown.
     *
     * This should usually be a short standalone action label. Defaults to "Newest".
     */
    newestLabel?: string
    /**
     * Label used for "go to oldest" action in the dropdown.
     *
     * This should usually be a short standalone action label. Defaults to "Oldest".
     */
    oldestLabel?: string
    /**
     * Label used as the range suffix while loading.
     *
     * This should usually be a very short loading placeholder because it is
     * inserted inline in the range display. Defaults to "...".
     */
    loadingRangeLabel?: string
}

/**
 * Pagination with known total items
 */
interface PaginationWithTotalItems extends BasePaginationProps {
    /**
     * Total number of items.
     * Shows "X-Y of Z" format and enables "Oldest" navigation.
     */
    totalItems: number
    /**
     * Not allowed when totalItems is provided (next page is calculated automatically)
     */
    defaultHasNext?: never
}

/**
 * Pagination without total items (cursor-based pagination)
 */
interface PaginationWithoutTotalItems extends BasePaginationProps {
    /**
     * Total number of items is not provided.
     * Shows "X-Y" format without total count.
     */
    totalItems?: never
    /**
     * Indicates if there's a next page (required when totalItems is not provided)
     */
    defaultHasNext: boolean
}

export type PaginationProps = PaginationWithTotalItems | PaginationWithoutTotalItems

const Pagination: FC<PaginationProps> = ({
    currentPage,
    totalItems,
    itemsPerPage = 50,
    onPageChange,
    sx,
    loading = false,
    defaultHasNext = false,
    currentPageItemCount,
    ofLabel = 'of',
    newestLabel = 'Newest',
    oldestLabel = 'Oldest',
    loadingRangeLabel = '...',
}) => {
    const totalPages = useMemo(
        () => (totalItems ? Math.ceil(totalItems / itemsPerPage) : undefined),
        [totalItems, itemsPerPage],
    )

    const startItem = useMemo(
        () => (currentPage - 1) * itemsPerPage + 1,
        [currentPage, itemsPerPage],
    )

    const endItem = useMemo(() => {
        // Calculate based on actual items on current page
        return (currentPage - 1) * itemsPerPage + currentPageItemCount
    }, [currentPage, itemsPerPage, totalItems, currentPageItemCount])

    const hasPrevious = currentPage > 1
    const hasNext = totalPages ? currentPage < totalPages : defaultHasNext
    const isFirstPage = currentPage === 1
    const isLastPage = totalPages ? currentPage === totalPages : false

    const handlePrevious = () => {
        if (hasPrevious && !loading) {
            onPageChange(currentPage - 1)
        }
    }

    const handleNext = () => {
        if (hasNext && !loading) {
            onPageChange(currentPage + 1)
        }
    }

    const handleGoToNewest = () => {
        if (!isFirstPage && !loading) {
            onPageChange(1)
        }
    }

    const handleGoToOldest = () => {
        if (!isLastPage && !loading && totalPages) {
            onPageChange(totalPages)
        }
    }

    if (totalItems === 0) {
        return null
    }

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center',
                }}
            >
                <Dropdown
                    buttonLabel={`${startItem}-${loading ? loadingRangeLabel : endItem}${totalItems ? ` ${ofLabel} ${totalItems.toLocaleString()}` : ''}`}
                    typographyVariant={'button'}
                    compact
                    menuAutoWidth
                    sx={{
                        background: 'none !important',
                        svg: {
                            display: 'none',
                        },
                    }}
                    paperSx={{
                        minWidth: '120px',
                    }}
                >
                    <DropdownItem onClick={handleGoToNewest} disabled={!hasPrevious || loading}>
                        {newestLabel}
                    </DropdownItem>
                    {totalPages && (
                        <DropdownItem onClick={handleGoToOldest} disabled={!hasNext || loading}>
                            {oldestLabel}
                        </DropdownItem>
                    )}
                </Dropdown>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}
            >
                <IconButton
                    style="floating"
                    size="sm"
                    leadingIcon={<ChevronLeftIcon />}
                    onClick={handlePrevious}
                    disabled={!hasPrevious || loading}
                    sx={{
                        '&:disabled': {
                            opacity: 0.4,
                            cursor: 'not-allowed',
                        },
                    }}
                />
                <IconButton
                    style="floating"
                    size="sm"
                    leadingIcon={<ChevronRightIcon />}
                    onClick={handleNext}
                    disabled={!hasNext || loading}
                    sx={{
                        '&:disabled': {
                            opacity: 0.4,
                            cursor: 'not-allowed',
                        },
                    }}
                />
            </Box>
        </Box>
    )
}

export default Pagination
